import { BaseShader } from './base-shader';
import { ShaderBehavior } from './shader-types';

/**
 * Abstract base class for geometric shaders that should scale proportionally.
 * These shaders represent discrete objects, shapes, or UI elements where maintaining
 * proportions and showing the complete object is more important than texture scale.
 *
 * Examples: spinning cube, geometric patterns, logos, UI elements
 */
export abstract class GeometricShader extends BaseShader {
  /**
   * Generate shader code that scales proportionally with canvas dimensions.
   * Geometric shaders should use aspect-ratio-corrected UV coordinates and
   * scale the entire object based on canvas size.
   */
  abstract generate(): string;

  /**
   * Get the shader code preamble that handles geometric-style UV calculation
   */
  protected getGeometricUVPreamble(): string {
    return `
      // Calculate aspect-ratio-corrected UV coordinates for geometric scaling
      vec2 baseUV = (gl_FragCoord.xy * 2.0 - u_resolution.xy) / min(u_resolution.x, u_resolution.y);

      // Apply rotation around center (0, 0)
      vec2 uv = baseUV;
      if (u_rotation != 0.0) {
        float angle = radians(u_rotation);
        float cosA = cos(angle);
        float sinA = sin(angle);
        uv = vec2(
          baseUV.x * cosA - baseUV.y * sinA,
          baseUV.x * sinA + baseUV.y * cosA
        );
      }
    `;
  }

  /**
   * Get the standard uniforms that geometric shaders need
   */
  protected getGeometricUniforms(): string {
    return `
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform float u_rotation;
    `;
  }
}
